import { Link } from "react-router-dom";
import { track } from "../../analytics";
import { GUIDES, type GuideSurface } from "../../components/guides";

/**
 * The user guide — the same per-surface content the SurfaceGuide dialogs teach in
 * place, rendered once as a page (route: /how).
 *
 * A page AND the dialogs, deliberately: the dialogs meet the confusion where it
 * happens, but a page is the thing an answer can LINK — a Discord reply, a Patreon
 * comment, the site menu — and it was asked for in exactly those words ("a how-to
 * page… goon-brained users would benefit from a user guide"). One content source
 * (guides.tsx), two renders, so the page cannot say something the dialogs don't.
 */

const SECTIONS: ReadonlyArray<{
  surface: GuideSurface;
  /** Where the reader goes to stand on the surface the section just explained. */
  go: { to: string; label: string };
  /** A second door onto the same surface, when the surface has two (the walls). */
  also?: { to: string; label: string };
}> = [
  { surface: "screen", go: { to: "/screen?code=0", label: "Open a screen" } },
  // A bare /screen IS the wall — the same address, so the wall section's link says so.
  // The Premium Wall shares the section (same rules, one difference) and needs naming.
  {
    surface: "wall",
    go: { to: "/screen?view=wall:main&code=0", label: "Walk onto the wall" },
    also: { to: "/screen?view=wall:premium&code=0", label: "Or the Premium Wall" },
  },
  {
    surface: "infinity",
    go: { to: "/screen?view=infinity:random&code=0", label: "Start with Random" },
    also: { to: "/screen?view=infinity:keyword&code=0", label: "Or Enhanced" },
  },
  { surface: "feed", go: { to: "/feed", label: "Open the Feed" } },
  { surface: "views", go: { to: "/views", label: "Go to your views" } },
  { surface: "editor", go: { to: "/views", label: "Build one" } },
];

export function HowTo() {
  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-8">
      <h1 className="text-3xl font-bold tracking-tight text-zinc-100">How to goon</h1>
      <p className="mt-2 text-sm text-zinc-400">
        Every page also explains itself — press the <span aria-hidden>ⓘ</span> in the bar
        or beside a heading whenever one of these is in front of you.
      </p>

      {SECTIONS.map(({ surface, go, also }) => {
        const g = GUIDES[surface];
        return (
          <section key={surface} className="mt-10">
            <h2 className="text-xl font-semibold text-zinc-100">{g.headline}</h2>
            <p className="mt-1 text-sm text-zinc-400">{g.sub}</p>
            <ul className="mt-4 space-y-3">
              {g.steps.map((s) => (
                <li key={s.title} className="flex gap-3 rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)]/60 p-3.5">
                  <span className="mt-0.5 shrink-0 text-[var(--color-accent-soft)]" aria-hidden>
                    {s.icon}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-zinc-100">{s.title}</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-zinc-500">{s.body}</p>
                  </div>
                </li>
              ))}
            </ul>
            {/* The page's conversion moment: reading turned into going. Tracked here and
                not on the pageview, because arrival is what a shared link buys and this
                is what the page earned. Fires on modified clicks too — a new tab is
                still a conversion. */}
            <Link
              to={go.to}
              onClick={() => track("howto_link_clicked", { surface })}
              className="mt-3 inline-block text-sm font-medium text-[var(--color-accent-soft)] underline-offset-4 hover:underline"
            >
              {go.label} &rarr;
            </Link>
            {also ? (
              <Link
                to={also.to}
                onClick={() => track("howto_link_clicked", { surface })}
                className="mt-3 ml-4 inline-block text-sm font-medium text-[var(--color-accent-soft)] underline-offset-4 hover:underline"
              >
                {also.label} &rarr;
              </Link>
            ) : null}
          </section>
        );
      })}

      {/* The one flow that spans two devices, said once. Deliberately no domain — a LAN
          install's address differs, and every surface that shows a code names the
          address beside it (hostLabel). */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold text-zinc-100">More screens, one remote</h2>
        <p className="mt-1 text-sm leading-relaxed text-zinc-400">
          Open goonlords on any TV, laptop or tablet and pick <span className="text-zinc-200">Screen</span> —
          it shows a short code. On your phone, open <span className="text-zinc-200">/remote</span> and type
          that code: the phone now drives that screen. Repeat for every screen in the room.
        </p>
      </section>

      {/* The binding money fine print lives in the buy dialog, stated at the moment it
          matters — the wall section above says only what that fine print binds. */}
    </div>
  );
}
